import { useMutation, useQuery } from '@apollo/client';
import { GET_CAMPAIGNS, GET_CAMPAIGN } from '../../../queries/campaigns';
import { CREATE_CAMPAIGN, UPDATE_CAMPAIGN, DELETE_CAMPAIGN } from '../../../mutations/campaigns';
import type {
  CampaignStatus,
  CreateCampaignInput,
  GetCampaignsQuery,
  GetCampaignQuery,
  CreateCampaignMutation,
  UpdateCampaignMutation,
  DeleteCampaignMutation,
  UpdateCampaignMutationVariables
} from '../../../gql/graphql';

export type Campaign = NonNullable<GetCampaignQuery['campaign']>;

export interface CampaignHookResult {
  campaigns: Campaign[];
  loading: boolean;
  error?: Error;
  refetch: () => Promise<void>;
}

export interface SingleCampaignHookResult {
  campaign?: Campaign;
  loading: boolean;
  error?: Error;
  refetch: () => Promise<void>;
}

export const useCampaigns = (merchantId: string): CampaignHookResult => {
  const {
    data,
    loading,
    error,
    refetch: apolloRefetch
  } = useQuery<GetCampaignsQuery>(GET_CAMPAIGNS, {
    variables: { merchantId }
  });

  const refetch = async () => {
    try {
      await apolloRefetch();
    } catch (e) {
      console.error('Failed to refetch campaigns:', e);
    }
  };

  return {
    campaigns: data?.campaigns ?? [],
    loading,
    error: error as Error | undefined,
    refetch
  };
};

export const useCampaign = (id: string): SingleCampaignHookResult => {
  const {
    data,
    loading,
    error,
    refetch: apolloRefetch
  } = useQuery<GetCampaignQuery>(GET_CAMPAIGN, {
    variables: { id }
  });

  const refetch = async () => {
    try {
      await apolloRefetch();
    } catch (e) {
      console.error('Failed to refetch campaign:', e);
    }
  };

  return {
    campaign: data?.campaign ?? undefined,
    loading,
    error: error as Error | undefined,
    refetch
  };
};

export const useCreateCampaign = (merchantId: string) => {
  const [createCampaign, { loading }] = useMutation<CreateCampaignMutation>(CREATE_CAMPAIGN, {
    refetchQueries: [{ query: GET_CAMPAIGNS, variables: { merchantId } }]
  });

  const create = async (input: CreateCampaignInput) => {
    try {
      const { data } = await createCampaign({
        variables: { input }
      });
      return data?.createCampaign;
    } catch (error) {
      throw error as Error;
    }
  };

  return { create, loading };
};

export const useUpdateCampaign = () => {
  const [updateCampaign, { loading, error }] = useMutation<
    UpdateCampaignMutation,
    UpdateCampaignMutationVariables
  >(UPDATE_CAMPAIGN);

  const updateCampaignFn = async (
    campaignId: string,
    input: UpdateCampaignMutationVariables['input']
  ) => {
    try {
      const { data } = await updateCampaign({
        variables: { updateCampaignId: campaignId, input }
      });
      return data?.updateCampaign;
    } catch (err) {
      console.error('Failed to update campaign:', err);
      throw err;
    }
  };

  return {
    updateCampaign: updateCampaignFn,
    loading,
    error
  };
};

export const useDeleteCampaign = (merchantId: string) => {
  const [deleteCampaign, { loading }] = useMutation<DeleteCampaignMutation>(DELETE_CAMPAIGN, {
    refetchQueries: [{ query: GET_CAMPAIGNS, variables: { merchantId } }]
  });

  const remove = async (campaignId: string) => {
    try {
      const { data } = await deleteCampaign({
        variables: { campaignId }
      });
      return data?.deleteCampaign;
    } catch (error) {
      throw error as Error;
    }
  };

  return { remove, loading };
};

export const useUpdateCampaignStatus = () => {
  const [updateCampaignStatus, { loading }] = useMutation<UpdateCampaignMutation>(UPDATE_CAMPAIGN);

  const update = async (campaignId: string, status: CampaignStatus) => {
    try {
      const { data } = await updateCampaignStatus({
        variables: { updateCampaignId: campaignId, input: { status } }
      });
      return data?.updateCampaign.status;
    } catch (error) {
      throw error as Error;
    }
  };

  return { update, loading };
};
